import { z } from "zod";

import { AuthResponseSchema, AuthUserSchema } from "@markeroo/shared";
import type { AuthResponse, AuthUser } from "@markeroo/shared";

import { apiAxios } from "./config";

const RawAuthResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    full_name: z.string(),
    picture_url: z.string().nullable().optional(),
    created_at: z.string(),
  }),
});

export type DevLoginRequest = {
  email: string;
  fullName?: string;
};

export async function devLogin(payload: DevLoginRequest): Promise<AuthResponse> {
  const response = await apiAxios.post("/auth/dev-login", payload);

  const data = RawAuthResponseSchema.parse(response.data);
  return AuthResponseSchema.parse({
    accessToken: data.access_token,
    tokenType: data.token_type,
    user: {
      id: data.user.id,
      email: data.user.email,
      fullName: data.user.full_name,
      pictureUrl: data.user.picture_url ?? null,
      createdAt: data.user.created_at,
    },
  });
}

// CRITICAL: This schema MUST include the impersonator field for admin impersonation to work.
// Without it, the impersonator data gets stripped when AuthBootstrap fetches the current user,
// causing the ImpersonationBanner to disappear after page reload.
const RawUserSchema: z.ZodType<{
  id: string;
  email: string;
  fullName: string;
  preferredName?: string | null;
  pictureUrl?: string | null;
  createdAt: string;
  isSuperuser?: boolean;
  isInternal?: boolean;
  preferences?: Record<string, unknown> | null;
  gcMode?: boolean;
  studentWritingEnabled?: boolean;
  dpaAcceptedAt?: string | null;
  dpaVersion?: string | null;
  impersonator?: {
    id: string;
    email: string;
    fullName: string;
    preferredName?: string | null;
    pictureUrl?: string | null;
    createdAt: string;
    isSuperuser?: boolean;
    isInternal?: boolean;
    preferences?: Record<string, unknown> | null;
    gcMode?: boolean;
    studentWritingEnabled?: boolean;
    dpaAcceptedAt?: string | null;
    dpaVersion?: string | null;
  } | null;
}> = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
  preferredName: z.string().nullable().optional(),
  pictureUrl: z.string().nullable().optional(),
  createdAt: z.string(),
  isSuperuser: z.boolean().optional(),
  isInternal: z.boolean().optional(),
  preferences: z.record(z.unknown()).nullable().optional(),
  gcMode: z.boolean().optional(),
  studentWritingEnabled: z.boolean().optional(),
  dpaAcceptedAt: z.string().nullable().optional(),
  dpaVersion: z.string().nullable().optional(),
  // CRITICAL: Required for impersonation feature - DO NOT REMOVE
  impersonator: z.lazy(() => RawUserSchema).nullable().optional(),
});

export async function fetchCurrentUser(): Promise<AuthUser> {
  const response = await apiAxios.get("/auth/me");

  const data = RawUserSchema.parse(response.data);
  return AuthUserSchema.parse({
    id: data.id,
    email: data.email,
    fullName: data.fullName,
    preferredName: data.preferredName ?? null,
    pictureUrl: data.pictureUrl ?? null,
    createdAt: data.createdAt,
    isSuperuser: data.isSuperuser ?? false,
    isInternal: data.isInternal ?? false,
    preferences: data.preferences ?? undefined,
    gcMode: data.gcMode ?? false,
    studentWritingEnabled: data.studentWritingEnabled ?? false,
    dpaAcceptedAt: data.dpaAcceptedAt ?? null,
    dpaVersion: data.dpaVersion ?? null,
    // CRITICAL: Required for impersonation banner to show - DO NOT REMOVE
    impersonator: data.impersonator ? {
      id: data.impersonator.id,
      email: data.impersonator.email,
      fullName: data.impersonator.fullName,
      preferredName: data.impersonator.preferredName ?? null,
      pictureUrl: data.impersonator.pictureUrl ?? null,
      createdAt: data.impersonator.createdAt,
      isSuperuser: data.impersonator.isSuperuser ?? false,
      isInternal: data.impersonator.isInternal ?? false,
      preferences: data.impersonator.preferences ?? undefined,
      gcMode: data.impersonator.gcMode ?? false,
      studentWritingEnabled: data.impersonator.studentWritingEnabled ?? false,
      dpaAcceptedAt: data.impersonator.dpaAcceptedAt ?? null,
      dpaVersion: data.impersonator.dpaVersion ?? null,
    } : null,
  });
}

export async function logoutSession(): Promise<void> {
  await apiAxios.post("/auth/logout");
}
